/** The Problems screen's words: files, pages and documents, never units; whose move it is and what staff can do. Pure. */
import type { Formatter } from '@paige/format';
import { placeName, unitWord } from '../flow/flow-words';
import type { FlowPlace, FlowUnit } from '../flow/flow-types';
import { reasonPhrase } from '../insights/reason-words';
import type { ProblemAction, ProblemFile, ProblemGroup } from './problem-types';

const HEADLINE: Record<string, string> = {
  capture_parked: 'Scans and uploads the engine could not read',
  capture_set_aside: 'A capture set aside: reading it took the engine down twice',
  retries_exhausted: 'Out of tries; Paige is about to park it',
  unspecified: 'Stopped with no reason recorded',
};

/** What the reason means, for someone who has never seen the code. */
const MEANING: Record<string, string> = {
  source_file_unreadable:
    'Paige could not open the file: it is damaged, password-protected or not what its name says. Nothing was read and nothing was charged.',
  source_file_set_aside:
    'Opening the file crashed the page worker three times, so Paige set it aside to protect everyone else. Engineering looks at why.',
  unsupported_format: 'The file is in a format Paige does not read. Nothing was read.',
  render_failed: 'A page could not be turned into a picture to read. Usually passes on a retry.',
  ocr_failed: 'The page reader failed on these pages. Usually passes on a retry.',
  stage_timeout: 'A step ran past its time limit. Paige stopped it rather than leave it hanging.',
  ambiguous_unresolved:
    'Paige was interrupted while working on this and cannot tell whether it finished. Retry runs it again; nothing is sent twice.',
  destination_auth_failed:
    'The destination refused the saved sign-in. The company reconnects it; then these go out.',
  destination_mapping_invalid:
    'The destination refused a field or a value Paige sent. The company fixes the mapping; then these go out.',
  destination_unreachable: 'The destination did not answer after several tries.',
  capture_parked:
    'A scan, upload or email import failed in the engine several times in a row, so Paige stopped sending it. Putting it back reads only what was not read; nothing is charged twice.',
  capture_set_aside:
    'The engine host died twice while reading this capture, so Paige set it aside to protect every other company. Put it back only once engineering knows why.',
  retries_exhausted:
    'The last automatic try failed. Within a minute it is parked with its reason and can be acted on here.',
};

/** The row in plain words. */
export function groupHeadline(g: Pick<ProblemGroup, 'reason'>): string {
  const phrase = HEADLINE[g.reason] ?? reasonPhrase(g.reason);
  return phrase.charAt(0).toUpperCase() + phrase.slice(1);
}

/** What it means and what happens next, or null when the headline says it all. */
export function groupMeaning(g: Pick<ProblemGroup, 'reason'>): string | null {
  return MEANING[g.reason] ?? null;
}

/** "4 pages in 2 files", "3 documents", "1 capture": the item count is the one Stages and queues shows. */
export function countWords(
  f: Formatter,
  g: Pick<ProblemGroup, 'items' | 'files' | 'unit'>,
): string {
  const items = `${f.int(g.items)} ${unitWord(g.unit, g.items)}`;
  if (g.unit !== 'pages' || g.files === g.items) return items;
  return `${items} in ${f.int(g.files)} ${g.files === 1 ? 'file' : 'files'}`;
}

/** "Preparing pages", "Checking and Export". */
export function placeWords(places: string[]): string {
  const names = places.map((p) => placeName[p as FlowPlace] ?? p.replace(/_/g, ' '));
  return names.length <= 2
    ? names.join(' and ')
    : `${names.slice(0, -1).join(', ')} and ${names.at(-1)}`;
}

/** "AP invoices", "AP invoices and 2 more jobs", "No job". */
export function jobWords(g: Pick<ProblemGroup, 'jobName' | 'jobs'>): string {
  const name = g.jobName ?? 'No job';
  return g.jobs > 1 ? `${name} and ${g.jobs - 1} more ${g.jobs === 2 ? 'job' : 'jobs'}` : name;
}

const CUSTOMER_MOVE: Record<string, string> = {
  replace_file: 'The company sends a readable copy of the file.',
  reconnect_destination: 'The company reconnects the destination in its job settings.',
  fix_mapping: 'The company fixes the mapping in its job settings.',
  choose_destination: 'The company chooses the destination.',
  confirm_split: 'The company confirms it in review, or you retry it.',
  review: 'The company looks at it in review.',
  buy_credits: 'The company adds pages.',
  resume: 'The company resumes intake.',
  release_sender: 'The company releases the email.',
  retry: 'The company retries it from its job, or you do here.',
};

export interface NextStep {
  /** Whose move it is, in a word or two. */
  who: string;
  tone: 'customer' | 'support' | 'engineering';
  /** What that is, in one sentence. */
  line: string;
}

/** Whose move the row is and what it is; for the customer's, what staff can still do is the buttons beside it. */
export function nextStep(
  g: Pick<ProblemGroup, 'owner' | 'action' | 'can' | 'settling' | 'items' | 'reason'>,
): NextStep {
  if (g.settling >= g.items)
    return {
      who: 'Paige',
      tone: 'engineering',
      line: 'Nothing to do yet: Paige parks this within a minute.',
    };
  if (g.owner === 'customer') {
    const theirs = CUSTOMER_MOVE[g.action] ?? 'The company clears this from its own screens.';
    const yours = g.can.includes('resolve')
      ? ' You can close it with a note, or open the company.'
      : '';
    return { who: "Customer's move", tone: 'customer', line: theirs + yours };
  }
  const yours =
    g.reason === 'capture_set_aside'
      ? 'Find out why it took the engine down, then put it back.'
      : g.can.includes('retry')
        ? 'Retry it. If it parks again, engineering looks.'
        : 'Paige retries this by itself.';
  return g.owner === 'support'
    ? { who: 'Yours', tone: 'support', line: yours }
    : { who: 'Engineering', tone: 'engineering', line: yours };
}

interface ActionWords {
  /** The button on a row. */
  all: string;
  /** The button on one file. */
  one: string;
  /** What it does, for the confirm step. */
  effect: string;
  /** "Retried", for the result message. */
  done: string;
}

const UNIT_WORDS: Record<ProblemAction, ActionWords> = {
  retry: {
    all: 'Retry all',
    one: 'Retry',
    effect: 'Each goes back in line with its tries reset and is picked up again within seconds.',
    done: 'Retried',
  },
  resolve: {
    all: 'Resolve',
    one: 'Resolve',
    effect:
      'Each is closed here (nothing is deleted). Anything not delivered stays with the company to send again from its job.',
    done: 'Resolved',
  },
  acknowledge: {
    all: 'Acknowledge',
    one: 'Acknowledge',
    effect:
      'The row stays, marked as known, and the health monitor stops alerting on it until the date you pick.',
    done: 'Acknowledged',
  },
  unacknowledge: {
    all: 'Stop acknowledging',
    one: 'Stop acknowledging',
    effect: 'The health monitor alerts on this row again as it ages.',
    done: 'Acknowledgement taken back for',
  },
};

const CAPTURE_RETRY: ActionWords = {
  all: 'Put back',
  one: 'Put back',
  effect:
    'The engine is sent it again now. Only what was not read is read; nothing is charged twice.',
  done: 'Put back',
};

export function actionWords(g: Pick<ProblemGroup, 'kind'>, action: ProblemAction): ActionWords {
  return g.kind === 'capture' && action === 'retry' ? CAPTURE_RETRY : UNIT_WORDS[action];
}

/** A file by its name, or by what it is when the name is not shown. */
export function fileWords(
  f: Formatter,
  file: Pick<ProblemFile, 'name' | 'kind' | 'pages'>,
): string {
  if (file.name) return file.name;
  const what = file.kind === 'capture' ? 'A capture' : 'A file';
  return file.pages > 0
    ? `${what} of ${f.int(file.pages)} ${file.pages === 1 ? 'page' : 'pages'}`
    : what;
}

/** "Retried 4 pages", "Resolved 1 document": what the page says once the API answered. */
export function resultWords(f: Formatter, done: string, n: number, unit: FlowUnit): string {
  return `${done} ${f.int(n)} ${unitWord(unit, n)}`;
}
